import { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { getPdfjs } from "../../lib/pdfjs";

const BTN = "inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 bg-white text-[11px] font-semibold hover:bg-slate-100 disabled:opacity-40";

// PDF'i tarayıcı eklentisi / iframe olmadan PDF.js ile tuvale çizer (Chrome'un iframe PDF engeline takılmaz)
export default function PdfCanvasViewer({ blob, onError }) {
  const wrap = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [zoom, setZoom] = useState(1);          // 1 = genişliğe sığdır
  const [fitScale, setFitScale] = useState(1);
  const [rendered, setRendered] = useState(0);
  useEffect(() => {
    let cancelled = false, doc = null;
    setPdf(null); setRendered(0);
    blob.arrayBuffer().then(async (buf) => {
      const lib = await getPdfjs();
      doc = await lib.getDocument({ data: buf }).promise;
      if (cancelled) { doc.destroy(); return; }
      const p1 = await doc.getPage(1);
      const w = (wrap.current?.clientWidth || 800) - 32;
      setFitScale(w / p1.getViewport({ scale: 1 }).width);
      setPdf(doc);
    }).catch(() => { if (!cancelled) onError?.("PDF açılamadı"); });
    return () => { cancelled = true; setTimeout(() => doc?.destroy?.().catch?.(() => {}), 0); };
  }, [blob, onError]);
  useEffect(() => {
    if (!pdf || !wrap.current) return;
    let cancelled = false;
    const scale = fitScale * zoom * (window.devicePixelRatio || 1);
    const css = fitScale * zoom;
    (async () => {
      try {
        for (let i = 1; i <= pdf.numPages && !cancelled; i++) {
          const page = await pdf.getPage(i);
          const canvas = wrap.current?.querySelector(`canvas[data-page="${i}"]`);
          if (!canvas || cancelled) return;
          const vp = page.getViewport({ scale });
          canvas.width = vp.width; canvas.height = vp.height;
          canvas.style.width = `${page.getViewport({ scale: css }).width}px`;
          await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
          if (!cancelled) setRendered(i);
        }
      } catch (e) { /* panel kapatıldı / belge yok edildi (Transport destroyed) — sessizce bırak */ }
    })();
    return () => { cancelled = true; };
  }, [pdf, fitScale, zoom]);
  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-slate-200 bg-white text-[11px] text-slate-600">
        <button type="button" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.2).toFixed(2)))} className={BTN} data-testid="pdf-zoom-out" title="Uzaklaştır"><ZoomOut className="w-3 h-3" /></button>
        <span className="w-12 text-center font-semibold" data-testid="pdf-zoom-level">%{Math.round(zoom * 100)}</span>
        <button type="button" onClick={() => setZoom((z) => Math.min(3, +(z + 0.2).toFixed(2)))} className={BTN} data-testid="pdf-zoom-in" title="Yakınlaştır"><ZoomIn className="w-3 h-3" /></button>
        <button type="button" onClick={() => setZoom(1)} className={BTN} data-testid="pdf-zoom-fit" title="Genişliğe sığdır"><Maximize2 className="w-3 h-3" /> Sığdır</button>
        <span className="ml-auto" data-testid="pdf-page-count">{pdf ? `${pdf.numPages} sayfa${rendered < pdf.numPages ? ` · çiziliyor ${rendered}/${pdf.numPages}` : ""}` : "Açılıyor…"}</span>
      </div>
      <div ref={wrap} className="flex-1 overflow-auto p-4 space-y-4 bg-slate-200/60 min-h-0" data-testid="pdf-pages">
        {pdf && Array.from({ length: pdf.numPages }, (_, i) => <canvas key={i + 1} data-page={i + 1} className="block mx-auto bg-white shadow-md" data-testid={`pdf-page-${i + 1}`} />)}
      </div>
    </div>
  );
}
